/**
 * MODAL
 * Dialog
 */
import { useEffect, useState } from "react"
import { createPortal } from "react-dom"
import { WithTranslation, withTranslation } from "react-i18next"
import Space from "./space"
import Button from "./button"
import Tooltip from "./tooltip"
import { STATUS_LOADED, STATUS_SAVING, STATUS_SEARCHING } from "@/redux/_status.types"
import SearchBar from "./search-bar"

interface OwnProps {
  backgroundColor?: string
  children?: any
  disableKeyEvents?: boolean
  disableClickOutside?: boolean
  disableOverflow?: boolean
  isCloseButtonVisible?: boolean
  isLarge?: boolean
  isFullScreen?: boolean
  onClose?: Function
  onDelete?: Function
  onSearch?: Function
  onNext?: Function
  onNextCta?: string
  status?: string
  title?: React.ReactNode
  nextDisabled?: boolean
  nextDisabledTooltip?: string
  nextDisabledTooltipPosition?: "top" | "bottom" | "left" | "right"
  className?: string
  contentClassName?: string
  contentHeight?: number | string
  displayOwl?: boolean
  displayOwlTop?: number
  footer?: React.ReactNode
}

type Props = OwnProps & WithTranslation

function Modal(props: Props) {
  const { t } = props

  //Searching
  const [isSearching, setIsSearching] = useState(false)

  //Detect action
  //> Enter => go next
  //> Escape => close modal
  useEffect(() => {
    if (!props.disableKeyEvents) {
      const onKeyDown = (e) => {
        if (e.key === "Enter") {
          next()
        } else if (e.key === "Escape") {
          close()
        }
      }

      window.addEventListener("keydown", onKeyDown)
      return () => {
        window.removeEventListener("keydown", onKeyDown)
      }
    }
  })

  //Close dialog
  function close() {
    if (props.onClose) {
      props.onClose()
    }
  }

  //Clic outside trigger close
  function clickOutside() {
    if (!props.disableClickOutside) {
      close()
    }
  }

  //Click on delete
  function deleteItem() {
    if (props.onDelete) {
      props.onDelete()
    }
  }

  //Custom style
  function getStyle() {
    const style: any = {
      backgroundColor: props.backgroundColor,
      minWidth: props.isFullScreen ? undefined : props.isLarge ? "780px" : undefined,
      maxWidth: props.isFullScreen ? undefined : props.isLarge ? "780px" : undefined
    }

    if (props.displayOwl) {
      style.backgroundImage = `url(${require("@/assets/owl.png")})`
      style.backgroundRepeat = "no-repeat"
      style.backgroundPosition = "-64px " + (props.displayOwlTop ? props.displayOwlTop : "8") + "px"
      style.backgroundSize = "260px auto"
      style.borderRadius = "12px"
      style.overflow = "hidden"
    }

    return style
  }

  //Function next
  function next() {
    if (props.onNext) {
      props.onNext()
    }
  }

  //Search
  function search(value) {
    setIsSearching(false)
    if (props.onSearch) props.onSearch(value)
  }

  const content = (
    <div className="modal-container flex" onClick={() => clickOutside()}>
      <div
        className={`${props.isFullScreen ? "modal-fullscreen" : "modal"} ${props.className || ""}`}
        onClick={(e) => {
          e.stopPropagation()
        }}
        style={getStyle()}>
        <div className="flex" style={props.title ? { minHeight: 52 } : undefined}>
          {props.title && <h1>{props.title}</h1>}

          <Space />

          {props.onSearch && (
            <div style={{ width: "300px", marginLeft: "80px" }}>
              <SearchBar
                onSearch={search}
                status={isSearching ? STATUS_SEARCHING : STATUS_LOADED}
                onSearchStart={() => setIsSearching(true)}
              />
            </div>
          )}
        </div>

        <div
          className={`${props.isFullScreen ? "modal-content-fullscreen" : "modal-content"} ${props.contentClassName || ""}`}
          style={{ overflowY: props.disableOverflow ? "initial" : "auto", maxHeight: props.contentHeight ?? undefined }}>
          {props.children}
        </div>

        <div className="flex">
          {props.onDelete && <Button onClick={deleteItem}>{t("utils_delete")}</Button>}

          <Space />

          {props.isCloseButtonVisible && <Button onClick={close}>{t("utils_close")}</Button>}

          {props.onNext && (
            <Tooltip text={props.nextDisabledTooltip} position={props.nextDisabledTooltipPosition ?? "top"}>
              <Button
                className="primary"
                isLoading={props.status === STATUS_SAVING}
                onClick={next}
                disabled={props.nextDisabled}>
                {props.onNextCta ? props.onNextCta : t("utils_next")}
              </Button>
            </Tooltip>
          )}

          {props.footer}
        </div>
      </div>
    </div>
  )

  return props.disableClickOutside ? createPortal(content, document.body) : content
}

export default withTranslation()(Modal)
